import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { IEmployee } from 'typings/employee';
import { Amount } from 'nomst/Stores/Decision/amount';
import { AmountCode } from 'typings/enums';
import { CountryBudget } from 'nomst/Stores/Decision/countryBudget';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { ICycleBudget } from 'typings/cycleBudget';

export interface IBudgetCalculationResult {
    amountCycleId: number;
    budgetCycleId: number;
    shouldResetAmount: boolean;
    originalBudget: number;
    newBudget: number;
    isOriginalBudgetEligible: boolean;
    isNewBudgetEligible: boolean;
}

export interface ITeamBudgetCalculationResult {
    budgetCycleId: number;
    newBudget: number;
    newEmployeesTotalPay: number;
}

export interface IManagerBudgetSummary {
    budgetCycleId: number;
    directReportsTotalCurrentPay: number;
    directReportsTotalBudget: number;
    directReportsBudgetPercentage: number;
}

export class EligibilityCalculator {
    // tslint:disable:parameters-max-number
    async recalculateEmployeeBudget(
        masterAccountId: string,
        cycleId: number,
        currencyStore: CurrencyConversion,
        countryCurrency: string,
        employee: IEmployee,
        peers: IEmployee[],
        budgetCycleId: number,
        employeeAmounts: Nullable<Amount[]>,
        budgets: ICycleBudget[]): Promise<Nullable<IBudgetCalculationResult>> {

        if (!employeeAmounts) {
            return null;
        }

        const employeeBudget = employee.budgets ?
            employee.budgets.find(budget => budget.budgetCycleId === budgetCycleId)
            || {} as CountryBudget
            : {} as CountryBudget;

        // Reset the amount decision to 0 when switching eligibility always.
        const shouldResetAmount = true;

        const multipleAmountsInSameBudget = employee?.amounts?.filter((amount: Amount) =>
            amount.budgetCycleId === employeeBudget.budgetCycleId) as Amount[];

        const matchedEmployAmount = employee?.amounts?.filter((amount: Amount) =>
            amount.budgetCycleId === budgetCycleId) as Amount[];

        const budgetResult  = {} as IBudgetCalculationResult;
        if ( multipleAmountsInSameBudget.length <= 1 ) {
            return this.getBudgetCalculationResultForSingleAmountBudgets(
                masterAccountId,
                multipleAmountsInSameBudget,
                cycleId,
                employee,
                budgetCycleId,
                matchedEmployAmount,
                shouldResetAmount,
                employeeBudget);
        }

        // If you reached this point, there are multiple amounts associated with the same budget.
        return this.getBudgetResultForMultiAmounts(
            multipleAmountsInSameBudget as Amount[],
            budgets,
            budgetCycleId,
            budgetResult,
            shouldResetAmount,
            employeeBudget,
            employeeAmounts,
            employee);
    }

    async getBudgetCalculationResultForSingleAmountBudgets(
        masterAccountId: string,
        multipleAmountsInSameBudget: Amount[],
        cycleId: number,
        employee: IEmployee,
        budgetCycleId: number,
        employeeAmount: Amount[],
        shouldResetAmount: boolean,
        employeeBudget: ICountryBudget
    ): Promise<Nullable<IBudgetCalculationResult>> {

        let newBudgetAmount: Nullable<number> = 0;
        const budgetResult  = {} as IBudgetCalculationResult;
        // case when marking employee back to eligible,
        // corresponding employeeBudget doesn't exist and
        // therefore multipleAmountsInSameBudget will be empty.
        // re-add the amount back to the budget when marked eligible
        if (multipleAmountsInSameBudget.length === 0) {
            const employeeDataManager = dataManagerFactory.getEmployeeDataManager();
            newBudgetAmount = await employeeDataManager.getEmployeeLastEligibleBudget(
                masterAccountId,
                cycleId,
                employee.employeeSourceId,
                budgetCycleId);

            this.saveBudgetCalculationResult(
                budgetCycleId,
                employeeAmount[0],
                newBudgetAmount || 0,
                0,
                shouldResetAmount,
                budgetResult,
                false,
                true);

            return budgetResult;
        }
        // case when marking employee ineligible
        if (multipleAmountsInSameBudget.length === 1) {
            // remove the amount from budget when marked ineligible
            this.saveBudgetCalculationResult(
                budgetCycleId,
                multipleAmountsInSameBudget[0],
                newBudgetAmount,
                employeeBudget.amount || 0,
                shouldResetAmount,
                budgetResult,
                multipleAmountsInSameBudget[0].initialIsEligible,
                multipleAmountsInSameBudget[0].isEligible);
            return budgetResult;
        }

        return budgetResult;
    }

    getNewEmployeeBudgetBasedOnPeers(
        currencyStore: CurrencyConversion,
        countryCurrency: string,
        employee: IEmployee,
        peers: IEmployee[],
        employeeBudget: CountryBudget): number {
        const managerBudgetSummary = this.getManagerBudgetSummaryInCountryCurrency(
            currencyStore,
            countryCurrency,
            employeeBudget.budgetCycleId,
            peers);

        const convertedEmployeeAnnualCurrentPay = currencyStore.convertCurrency(
            employee.isoCurrencyCode,
            countryCurrency,
            employee.annualizedCurrentPay ?? 0);

        const newManagerDirectReportsTotalCurrentPay =
            (managerBudgetSummary.directReportsTotalCurrentPay + convertedEmployeeAnnualCurrentPay);

        const newManagerDirectReportsBudget =
            newManagerDirectReportsTotalCurrentPay * managerBudgetSummary.directReportsBudgetPercentage;

        const newEmployeeBudgetInCountryCurrency =
            newManagerDirectReportsBudget - managerBudgetSummary.directReportsTotalBudget;

        return currencyStore.convertCurrency(
            countryCurrency,
            employee.currentIsoCurrencyCode ?? '',
            newEmployeeBudgetInCountryCurrency);
    }

    getManagerBudgetSummaryInCountryCurrency(
        currencyStore: CurrencyConversion,
        countryCurrency: string,
        budgetCycleId: number,
        directReports: IEmployee[]): IManagerBudgetSummary {
        let directReportsTotalCurrentPay = 0;
        let directReportsTotalBudget = 0;
        const eligibleDirectReports = directReports.filter(directReport =>
            this.isBudgetEligible((directReport.amounts || []).filter(
                amount => amount.budgetCycleId === budgetCycleId) as Amount[]));

        eligibleDirectReports.forEach(directReport => {
            directReportsTotalCurrentPay += currencyStore.convertCurrency(
                directReport.isoCurrencyCode,
                countryCurrency,
                directReport.annualizedCurrentPay ?? 0);

            const directReportBudget =
                (directReport.amounts || []).find(budget => budget.budgetCycleId === budgetCycleId) as Amount;
            if (!directReportBudget) {
                return;
            }

            directReportsTotalBudget += currencyStore.convertCurrency(
                directReport.isoCurrencyCode,
                countryCurrency,
                directReportBudget.value);
        });

        return {
            budgetCycleId,
            directReportsTotalCurrentPay,
            directReportsTotalBudget,
            directReportsBudgetPercentage: directReportsTotalCurrentPay === 0 ?
                0 :
                directReportsTotalBudget / directReportsTotalCurrentPay
        } as IManagerBudgetSummary;
    }

    private getBudgetResultForMultiAmounts(
        multipleAmountsInSameBudget: Amount[],
        budgets: ICycleBudget[],
        budgetCycleId: number,
        budgetResult: IBudgetCalculationResult,
        shouldResetAmount: boolean,
        employeeBudget: ICountryBudget,
        employeeAmounts: Amount[],
        employee: IEmployee): IBudgetCalculationResult {
        const isOtherAmountsEligible = this.isBudgetEligible(multipleAmountsInSameBudget as Amount[]);
        const isLabSyncOnForBudget = (budgets
            .find(budget => budget.budgetCycleId === budgetCycleId) as ICycleBudget).autoSync;

        // If the lab auto sync is off, then it is treated as single pool of money.
        // When budget has other eligible amounts, don't update the budgets.
        // Only reset the decision amounts to 0.
        if (isOtherAmountsEligible && !isLabSyncOnForBudget) {
            budgetResult.shouldResetAmount = shouldResetAmount;
            return budgetResult;
        }

        // When budget has no eligible amounts, update the budgets.
        // And reset the decision amounts to 0.
        if (!isOtherAmountsEligible && !isLabSyncOnForBudget) {
            this.saveBudgetCalculationResult(
                budgetCycleId,
                null,
                0,
                employeeBudget.amount || 0,
                shouldResetAmount,
                budgetResult,
                true,
                false);
            return budgetResult;
        }

        // if the sync is on,
        // then the corresponding budget value must be
        // either removed or added to the budget.
        const originalBudgetAmount = employeeBudget.amount || 0;

        if (isLabSyncOnForBudget) {
            return this.getBudgetResultWhenSyncIsOn(
                employeeAmounts,
                employee,
                originalBudgetAmount,
                budgetResult,
                shouldResetAmount,
                budgetCycleId);
        }

        return budgetResult;
    }

    private CalculateLabSyncedAmounts(
        employeeAmounts: Amount[],
        employee: IEmployee,
        originalBudgetAmount: number
    ): number {
        let newBudgetAmount = originalBudgetAmount;
        for (const amount of employeeAmounts) {
            if (amount.amountCode === AmountCode.Merit
                && employee.meritRecommendation
                && employee.meritRecommendation > 0) {
                newBudgetAmount +=
                    this.getAmountValueBasedonEligibility(amount.initialIsEligible, employee.meritRecommendation);
            } else if (
                amount.amountCode === AmountCode.Bonus && employee.plannedBonus &&
                employee.plannedBonus > 0) {
                newBudgetAmount +=
                    this.getAmountValueBasedonEligibility(amount.initialIsEligible, employee.plannedBonus);
            } else if (
                amount.amountCode === AmountCode.VariablePay && employee.plannedVariable &&
                employee.plannedVariable > 0) {
                newBudgetAmount +=
                    this.getAmountValueBasedonEligibility(amount.initialIsEligible, employee.plannedVariable);
            }
        }

        return newBudgetAmount;
    }

    private getAmountValueBasedonEligibility(
        initialIsEligible: boolean,
        amountValue: number
    ): number {
        return initialIsEligible ? -amountValue : amountValue;
    }

    private getBudgetResultWhenSyncIsOn(
        employeeAmounts: Amount[],
        employee: IEmployee,
        originalBudgetAmount: number,
        budgetResult: IBudgetCalculationResult,
        shouldResetAmount: boolean,
        budgetCycleId: number): IBudgetCalculationResult {
        let newBudgetAmount = this.CalculateLabSyncedAmounts(employeeAmounts, employee, originalBudgetAmount);

        if (!newBudgetAmount) {
            newBudgetAmount = 0;
        }

        if (newBudgetAmount === originalBudgetAmount) {
            return budgetResult;
        }

        budgetResult.shouldResetAmount = shouldResetAmount;
        budgetResult.budgetCycleId = budgetCycleId;
        budgetResult.originalBudget = originalBudgetAmount;
        budgetResult.newBudget = newBudgetAmount;
        return budgetResult;
    }
    private isBudgetEligible(budgetAmounts: Amount[]): boolean {
        if (!budgetAmounts || budgetAmounts.length === 0) {
            return true;
        }

        return budgetAmounts.find(amount => amount.isEligible) !== undefined;
    }

    private saveBudgetCalculationResult(
        budgetCycleId: number,
        employeeAmount: Nullable<Amount>,
        newBudgetAmount: number,
        originalBudget: number,
        shouldResetAmount: boolean,
        budgetResult: IBudgetCalculationResult,
        isOriginalBudgetEligible: boolean,
        isNewBudgetEligible: boolean): void {
        budgetResult.budgetCycleId = budgetCycleId;
        budgetResult.shouldResetAmount = shouldResetAmount;
        if (employeeAmount) {
            budgetResult.amountCycleId = employeeAmount.amountCycleId;
        }
        budgetResult.isOriginalBudgetEligible = isOriginalBudgetEligible;
        budgetResult.isNewBudgetEligible = isNewBudgetEligible;
        budgetResult.newBudget = newBudgetAmount;
        budgetResult.originalBudget = originalBudget;
    }
}

export default new EligibilityCalculator();